import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  dismissAlert,
  fetchAlerts,
  fetchUnreadCount,
  markAlertRead,
  markAllAlertsRead,
} from '../utils/adminAlerts';

const AdminNotificationContext = createContext(null);

const POLL_MS = 20000;

export function AdminNotificationProvider({ children }) {
  const [unread, setUnread] = useState(0);
  const [byCategory, setByCategory] = useState({});
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const data = await fetchUnreadCount();
      setUnread(data.unread || 0);
      setByCategory(data.by_category || {});
    } catch (_) {
      /* not logged in */
    }
  }, []);

  const refreshRecent = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAlerts({ unreadOnly: false, limit: 15 });
      setRecent(data.alerts || []);
    } catch (_) {
      setRecent([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([refreshCount(), refreshRecent()]);
  }, [refreshCount, refreshRecent]);

  useEffect(() => {
    refresh();
    const id = setInterval(refreshCount, POLL_MS);
    return () => clearInterval(id);
  }, [refresh, refreshCount]);

  const readOne = useCallback(async (alertId) => {
    await markAlertRead(alertId);
    await refresh();
  }, [refresh]);

  const readAll = useCallback(async () => {
    await markAllAlertsRead();
    await refresh();
  }, [refresh]);

  const dismissOne = useCallback(async (alertId) => {
    await dismissAlert(alertId);
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      unread,
      byCategory,
      recent,
      loading,
      refresh,
      refreshCount,
      readOne,
      readAll,
      dismissOne,
    }),
    [unread, byCategory, recent, loading, refresh, refreshCount, readOne, readAll, dismissOne],
  );

  return (
    <AdminNotificationContext.Provider value={value}>
      {children}
    </AdminNotificationContext.Provider>
  );
}

export function useAdminNotifications() {
  const ctx = useContext(AdminNotificationContext);
  if (!ctx) {
    throw new Error('useAdminNotifications must be used within AdminNotificationProvider');
  }
  return ctx;
}
